import { Room as RoomContainer } from '@features/rooms/pages/Room';
import { Property } from 'shared-types/Property';
import { NormalizedStore } from '@store/entity/entity.types';
import { NextPage, NextPageContext } from 'next';
import NextSeo from 'next-seo';
import Head from 'next/head';
import React, { useEffect } from 'react';
import { Router } from 'shared-routing';
import { analytics } from '@features/analytics';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { getStaticRates, ratesKeys } from '@features/rates';
import {
  getAvailableRoomById,
  getStaticRoomById,
  roomsKeys,
  useStaticRoomById,
} from '@features/rooms';
import { PagePropsWithSSR } from '@features/state';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { parseDateToString } from '@utils/helpers';

type RoomPageProps = {
  id: string;
  property: Property;
  language: string;
  url: string;
};

const RoomPage: NextPage<RoomPageProps> = ({ id, property, language, url }) => {
  const { data: room } = useStaticRoomById(id, language);
  const RoomTitle = room?.title;
  const PropertyTitle = property.propertyTitle;

  const pageTitle = `${RoomTitle} ${PropertyTitle ? `| ${PropertyTitle}` : ''}`;

  useEffect(() => {
    analytics.track({
      event: 'view_item',
      params: {
        items: [{ item_category: 'rooms', item_id: room?.room_id, item_name: room?.title }],
      },
    });
  }, []);

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            description: room ? room.description : '',
            locale: language,
            url,
            images: [
              {
                url:
                  room && room.image_url && room.image_url.length
                    ? room.image_url
                    : property.socialFallbackImage?.url || '',
              },
            ],
          },
        }}
      />
      <RoomContainer language={language} property={property} />
    </>
  );
};

type RoomInitialProps = {
  store: NormalizedStore;
} & NextPageContext &
  Pick<RoomPageProps, 'language' | 'url' | 'property'>;

RoomPage.getInitialProps = async (
  props: RoomInitialProps
): Promise<PagePropsWithSSR<RoomPageProps>> => {
  const { store, property, language, url, res, query, moduleStatuses } = props;
  const { id: siteId, slug: siteSlug } = property;

  if (!moduleStatuses.roomsEnabled) {
    if (res) {
      res.writeHead(302, {
        Location: '/',
      });
      res.end();
    } else {
      Router.push('/');
    }
  }

  const roomId = query.id as string;

  if (!res) {
    return {
      id: roomId,
      property,
      language,
      url,
    };
  }

  const state = store.getState();
  const journeyParams = yourJourneySelectors.getParsedJourneyParams(state);

  const adults = journeyParams.adults;
  const children = journeyParams.children;
  const childAges = journeyParams.childAges;
  const fromDate = parseDateToString(journeyParams.fromDate);
  const toDate = parseDateToString(journeyParams.toDate);
  const rooms = journeyParams.rooms;

  const client = new QueryClient();

  const staticRoomQuery = client.prefetchQuery({
    queryKey: roomsKeys.staticRoomById({ siteId: property.id, id: roomId, language }),
    queryFn: () => getStaticRoomById({ siteId: property.id, id: roomId, language }),
    cacheTime: Infinity,
    staleTime: Infinity,
  });

  const availableRoomQuery = client.prefetchQuery({
    queryKey: roomsKeys.availableRoomById({
      id: roomId,
      siteId,
      rooms,
      adults,
      children,
      childAges,
      fromDate,
      toDate,
      isMultiRoomsEnabled: rooms.length > 1,
    }),
    queryFn: () =>
      getAvailableRoomById({
        id: roomId,
        siteId,
        rooms,
        adults,
        children,
        childAges,
        fromDate,
        toDate,
        isMultiRoomsEnabled: rooms.length > 1,
      }),
  });

  const staticRatesQuery = client.prefetchQuery({
    queryKey: ratesKeys.staticRates({ siteSlug, language }),
    queryFn: () => getStaticRates({ siteSlug, language }),
    cacheTime: Infinity,
    staleTime: Infinity,
  });

  await Promise.all([staticRoomQuery, staticRatesQuery, availableRoomQuery]);

  return {
    id: roomId,
    property,
    language,
    url,
    dehydratedState: dehydrate(client),
  };
};

export default RoomPage;
